import { NotificationType, useTranslation, useUserVoices } from "@next-platform/client-common-core";
import { TFunction } from "i18next";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import useToast from "@/hooks/useToast";
import { CUSTOM_VOICES_LIMIT_ERROR_CODE, VOICE_CREATION_RATE_LIMIT_ERROR_CODE } from "@/pages/Voice/constants";
import { VoiceFormPayload } from "@/pages/Voice/types";

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
function getVoiceCloneError(t: TFunction): Record<"FAIL_INIT" | "NOT_PREMIUM" | "QUOTA_EXCEEDED" | "RATE_LIMIT_EXCEEDED", string> {
  return {
    FAIL_INIT: t("voice:cloneVoice.error.failInit"),
    NOT_PREMIUM: t("voice:cloneVoice.error.notPremium"),
    QUOTA_EXCEEDED: t("voice:cloneVoice.error.quotaExceeded"),
    RATE_LIMIT_EXCEEDED: t("voice:cloneVoice.error.rateLimitExceeded"),
  } as const;
}

type CloneVoiceResult = {
  error?: string | null;
  errorPayload?: { errorCode?: string | null } | null;
  errorType?: string | null;
};

type CloneErrorResolution = {
  resultError: string | null | undefined;
  shouldNotifyError: boolean;
};

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
function resolveCloneVoiceError(result: CloneVoiceResult, t: TFunction): CloneErrorResolution {
  const isQuotaError = result.errorPayload?.errorCode === CUSTOM_VOICES_LIMIT_ERROR_CODE;
  const isRateLimitError = result.errorPayload?.errorCode === VOICE_CREATION_RATE_LIMIT_ERROR_CODE;

  let resultError: string | null | undefined;
  if (isQuotaError) resultError = getVoiceCloneError(t).QUOTA_EXCEEDED;
  else if (isRateLimitError) resultError = getVoiceCloneError(t).RATE_LIMIT_EXCEEDED;
  else resultError = result.error;

  return { resultError, shouldNotifyError: Boolean(resultError) };
}

export default function useCloneVoiceHandler(recloneId: string | null, returnTo: string | null, hasCustomVoicesAccess: boolean) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const { cloneUserVoice, updateUserVoice } = useUserVoices();

  const onClone = useCallback(
    async (formPayload: VoiceFormPayload): Promise<{ success: boolean; error: string }> => {
      if (!hasCustomVoicesAccess) {
        const notPremiumMessage = getVoiceCloneError(t).NOT_PREMIUM;
        displayToast({ message: notPremiumMessage, type: NotificationType.error });
        return { success: false, error: notPremiumMessage };
      }
      try {
        let resultError: string | null | undefined;
        let shouldNotifyError = false;

        if (recloneId) {
          const result = await updateUserVoice({
            voice_id: recloneId,
            name: formPayload.name,
            description: formPayload.description,
            language: formPayload.language,
            sampleFiles: formPayload.sampleFiles,
          });
          resultError = result.error;
          shouldNotifyError = Boolean(resultError);
        } else {
          const result = await cloneUserVoice({
            name: formPayload.name,
            description: formPayload.description,
            language: formPayload.language,
            sampleFiles: formPayload.sampleFiles.filter((sample): sample is File => sample instanceof File),
          });
          const resolution = resolveCloneVoiceError(result, t);
          resultError = resolution.resultError;
          shouldNotifyError = resolution.shouldNotifyError;
        }

        if (shouldNotifyError && resultError) displayToast({ message: resultError, type: NotificationType.error });
        if (resultError) return { success: false, error: resultError };
        displayToast({ message: t("voice:cloneVoice.success.generating") });
        navigate(returnTo ? decodeURIComponent(returnTo) : ROUTES.voices);
        return { success: true, error: "" };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : getVoiceCloneError(t).FAIL_INIT;
        return { success: false, error: errorMessage };
      }
    },
    [cloneUserVoice, updateUserVoice, recloneId, displayToast, navigate, hasCustomVoicesAccess, returnTo, t],
  );

  return onClone;
}
